// Principals: machine identities in a tenant (docs/api.md 2.3).
import { useState, type FormEvent } from "react";
import type { Principal, PrincipalInput, TenantDetail } from "../api/types";
import { Badge } from "../components/Badge";
import { useServices } from "../components/context";
import { IdCell } from "../components/IdCell";
import { PendingFilter, useGrace, type PendingRow } from "../components/Grace";
import { CopyButton, ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { sortScopes } from "../lib/adminScopes";
import { AdminAccess } from "./AdminAccess";
import { formatTime } from "../lib/format";
import { API_KEY_LEAK_TEXT } from "../lib/grace";
import { TENANT_WIDE_HINT } from "../lib/namespaces";
import { atLeast } from "../lib/roles";

/** Parses claim lines "key=value". A key on more than one line becomes an array of values. */
export function parseClaims(text: string): { error: string } | { claims: Record<string, string | string[]> } {
  const claims: Record<string, string | string[]> = {};
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { error: "Enter at least one claim, for example project_path=acme/core." };
  for (const line of lines) {
    const i = line.indexOf("=");
    if (i <= 0 || i === line.length - 1) return { error: `Use key=value on each line: "${line}".` };
    const key = line.slice(0, i).trim();
    const value = line.slice(i + 1).trim();
    const prev = claims[key];
    claims[key] = prev === undefined ? value : Array.isArray(prev) ? [...prev, value] : [prev, value];
  }
  return { claims };
}

/** One-line description of how a principal matches. */
export function principalMatch(p: Principal): string {
  if (p.kind === "api_key") return p.api_key_hint ? `API key ending ${p.api_key_hint}` : "API key";
  if (p.kind === "spiffe") return `${p.match_type === "prefix" ? "prefix" : "exact"} ${p.spiffe_id ?? ""}`;
  return Object.entries(p.claims_match ?? {})
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join("|") : v}`)
    .join(", ");
}

function status(p: Principal, now: number): string {
  if (p.disabled) return "disabled";
  if (p.expires_at && Date.parse(p.expires_at) <= now) return "expired";
  return "active";
}

export function Principals({ tenant }: { tenant: TenantDetail }) {
  const { api } = useServices();
  const list = useLoad(() => api.listPrincipals(tenant.slug), [tenant.slug]);
  const tcs = useLoad(() => api.listTrustConfigs(tenant.slug), [tenant.slug]);
  const action = useAction();
  const isAdmin = atLeast(tenant.role, "admin");

  const [kind, setKind] = useState<PrincipalInput["kind"]>("api_key");
  const [name, setName] = useState("");
  const [tcId, setTcId] = useState("");
  const [spiffeId, setSpiffeId] = useState("");
  const [matchType, setMatchType] = useState<"exact" | "prefix">("exact");
  const [claims, setClaims] = useState("");
  const [days, setDays] = useState(30);
  const [formError, setFormError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null);

  const tcOfKind = (tcs.data?.items ?? []).filter((t) => t.kind === kind);

  function buildBody(): PrincipalInput | string {
    const n = name.trim();
    if (!n) return "Enter a name.";
    if (kind === "api_key") {
      if (!Number.isInteger(days) || days < 1 || days > 90) return "An API key expires in 1 to 90 days.";
      return { kind, name: n, expires_in_days: days };
    }
    const tc = tcId || tcOfKind[0]?.id;
    if (!tc) return `Add a ${kind === "spiffe" ? "SPIFFE" : "OIDC"} trust config first.`;
    if (kind === "spiffe") {
      if (!spiffeId.startsWith("spiffe://")) return "A SPIFFE ID starts with spiffe://.";
      return { kind, name: n, trust_config_id: tc, spiffe_id: spiffeId.trim(), match_type: matchType };
    }
    const c = parseClaims(claims);
    if ("error" in c) return c.error;
    return { kind, name: n, trust_config_id: tc, claims_match: c.claims };
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const body = buildBody();
    if (typeof body === "string") {
      setFormError(body);
      return;
    }
    setFormError(null);
    const ok = await action.run(async () => {
      const created = await api.createPrincipal(tenant.slug, body);
      if (created.api_key) setNewKey({ name: created.name, key: created.api_key });
    });
    if (ok) {
      setName("");
      setSpiffeId("");
      setClaims("");
      list.reload();
    }
  }

  const [adminFor, setAdminFor] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    if (await action.run(async () => void (await fn()))) list.reload();
  }

  const grace = useGrace<Principal>({ tenant, collection: "principals", onChange: list.reload });
  const pendingRows: PendingRow[] = grace.items.map((p) => ({
    id: p.id,
    title: (
      <>
        {p.name} <Badge kind={p.kind} />
      </>
    ),
    detail: principalMatch(p),
    warning: p.kind === "api_key" ? API_KEY_LEAK_TEXT : undefined,
    deletedBy: p.deleted_by,
    deletedAt: p.deleted_at,
    purgeAfter: p.purge_after,
    purge: {
      id: p.id,
      title: `principal "${p.name}"`,
      cascade: "Its grants are deleted too.",
      confirm: p.name,
      confirmLabel: "principal name",
    },
  }));

  const now = Date.now();
  const adminTarget = adminFor ? (list.data?.items ?? []).find((p) => p.id === adminFor) : undefined;

  return (
    <>
      {newKey && (
        <section className="card key-once" role="alert">
          <h2>API key for "{newKey.name}"</h2>
          <p className="banner warn">
            Copy this key now. Prego shows it only once. Store it in a secret store, not in a repository.
          </p>
          <div className="row">
            <code className="secret">{newKey.key}</code>
            <CopyButton text={newKey.key} />
          </div>
          <p className="muted">Use it as PREGO_API_KEY with cargo-credential-prego.</p>
          <button type="button" onClick={() => setNewKey(null)}>
            I stored the key
          </button>
        </section>
      )}
      <Section
        title="Principals"
        actions={<PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />}
      >
        <p className="muted">A principal is a machine identity. Give it grants to read or publish crates.</p>
        <p className="hint">{TENANT_WIDE_HINT}</p>
        <ErrorBanner error={list.error ?? action.error ?? grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : (
          <table className="stack data-table principal-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Match</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Admin scopes</th>
                <th>ID</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((p) => (
                <tr key={p.id}>
                  <td>
                    <strong>{p.name}</strong>
                  </td>
                  <td data-label="Kind">
                    <Badge kind={p.kind} />
                  </td>
                  <td className="mono" data-label="Match">
                    {principalMatch(p)}
                  </td>
                  <td data-label="Status">
                    <span className={`tag ${status(p, now)}`}>{status(p, now)}</span>
                  </td>
                  <td data-label="Expires">{formatTime(p.expires_at)}</td>
                  <td className="mono small" data-label="Admin scopes">
                    {p.admin_scopes?.length ? sortScopes(p.admin_scopes).join(" ") : "none"}
                  </td>
                  <IdCell id={p.id} />
                  <td className="actions" data-label="Actions">
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          className="secondary"
                          disabled={action.busy}
                          onClick={() =>
                            void act(() => api.updatePrincipal(tenant.slug, p.id, { disabled: !p.disabled }))
                          }
                        >
                          {p.disabled ? "Enable" : "Disable"}
                        </button>
                        <button
                          type="button"
                          className="secondary"
                          disabled={action.busy}
                          onClick={() =>
                            void act(
                              () => api.revokePrincipal(tenant.slug, p.id),
                              `Revoke "${p.name}"? Its live registry tokens stop working within about 60 seconds.`,
                            )
                          }
                        >
                          Revoke tokens
                        </button>
                        <button type="button" className="secondary" onClick={() => setAdminFor(p.id)}>
                          Admin access
                        </button>
                        <button
                          type="button"
                          className="danger"
                          disabled={action.busy || grace.busy}
                          onClick={() =>
                            grace.askDelete({
                              id: p.id,
                              title: `principal "${p.name}"`,
                              message:
                                "The STS refuses it at once, and its registry tokens stop working within about 60 seconds. Its grants stop too.",
                              impact: "principals",
                              inUseName: p.name,
                              inUseLabel: "principal name",
                            })
                          }
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {grace.ui}
      {isAdmin && adminTarget && (
        <AdminAccess
          key={adminTarget.id}
          tenantSlug={tenant.slug}
          principal={adminTarget}
          onSaved={() => {
            setAdminFor(null);
            list.reload();
          }}
          onClose={() => setAdminFor(null)}
        />
      )}
      {isAdmin && (
        <Section title="Add a principal">
          <form onSubmit={onCreate} className="form">
            <p className="hint">{TENANT_WIDE_HINT}</p>
            <label>
              Kind
              <select
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as PrincipalInput["kind"]);
                  setTcId("");
                }}
              >
                <option value="api_key">API key</option>
                <option value="oidc">OIDC (for example GitLab CI)</option>
                <option value="spiffe">SPIFFE</option>
              </select>
            </label>
            <label>
              Name
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ci builders"
              />
            </label>
            {kind === "api_key" ? (
              <label>
                Expires in (days, 1 to 90)
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                />
              </label>
            ) : (
              <label>
                Trust config
                <select value={tcId || tcOfKind[0]?.id || ""} onChange={(e) => setTcId(e.target.value)}>
                  {tcOfKind.length === 0 && <option value="">No {kind} trust config</option>}
                  {tcOfKind.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.spiffe_trust_domain ?? t.issuer})
                    </option>
                  ))}
                </select>
              </label>
            )}
            {kind === "spiffe" && (
              <>
                <label>
                  SPIFFE ID
                  <input
                    required
                    value={spiffeId}
                    onChange={(e) => setSpiffeId(e.target.value)}
                    placeholder="spiffe://prod.acme.io/ci/"
                  />
                </label>
                <label>
                  Match
                  <select
                    value={matchType}
                    onChange={(e) => setMatchType(e.target.value as "exact" | "prefix")}
                  >
                    <option value="exact">Exact</option>
                    <option value="prefix">Prefix</option>
                  </select>
                </label>
              </>
            )}
            {kind === "oidc" && (
              <label>
                Claims to match (one key=value per line; a value can end with *)
                <textarea
                  required
                  rows={4}
                  spellCheck={false}
                  value={claims}
                  onChange={(e) => setClaims(e.target.value)}
                  placeholder={"project_path=acme/core\nref_protected=true"}
                />
                <small className="muted">
                  If other tenants can use the same issuer (gitlab.com, GitHub), include a project or
                  repository claim.
                </small>
              </label>
            )}
            {formError && (
              <p className="banner error" role="alert">
                {formError}
              </p>
            )}
            <button type="submit" disabled={action.busy}>
              Add principal
            </button>
          </form>
        </Section>
      )}
    </>
  );
}
